import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { FiEdit, FiEye, FiEyeOff } from "react-icons/fi";
import { Categorie } from "../../../apiServices/categories.service";
import { Order } from "../hooks/useTableState";

interface AdminCategoryListProps {
  categories: Categorie[];
  onEdit: (category: Categorie) => void;
  onToggleStatus: (category: Categorie) => void;
  order: Order;
  orderBy: string;
  onRequestSort: (key: string) => void;
}

export default function AdminCategoryList({
  categories,
  onEdit,
  onToggleStatus,
  order,
  orderBy,
  onRequestSort,
}: AdminCategoryListProps) {
  return (
    <>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.12)",
            "& .MuiTableCell-head": {
              bgcolor: "background.default",
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            },
          }}
        >
          <Table sx={{ minWidth: 650, tableLayout:'fixed' }} aria-label="categorias table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "45%" }}>
                  <TableSortLabel
                    active={orderBy === "nombreEspanol" && order !== null}
                    direction={orderBy === "nombreEspanol" && order ? order : "asc"}
                    onClick={() => onRequestSort("nombreEspanol")}
                  >
                    NOMBRE
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: "30%" }}>
                  <TableSortLabel
                    active={orderBy === "activo" && order !== null}
                    direction={orderBy === "activo" && order ? order : "asc"}
                    onClick={() => onRequestSort("activo")}
                    sx={{ justifyContent: "center", width: "100%", display: "flex" }}
                  >
                    ESTADO
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "15%" }}align="right">ACCIONES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow
                  key={category.categoriaId}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { bgcolor: "background.default" },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ color: "text.primary", fontWeight: 500 }}
                  >
                    {category.nombreEspanol}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.activo ? "Activa" : "Inactiva"}
                      color={category.activo ? "primary" : "default"}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        borderRadius: "6px",
                        minWidth: "85px",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(category)}
                        sx={{ color: "text.secondary", mr: 1 }}
                      >
                        <FiEdit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={category.activo ? "Deshabilitar" : "Habilitar"}
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        onClick={() => onToggleStatus(category)}
                        sx={{ color: "text.secondary" }}
                      >
                        {category.activo ? <FiEyeOff /> : <FiEye />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 2,
        }}
      >
        {categories.map((category) => (
          <Paper
            key={category.categoriaId}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.12)",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Nombre
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.primary", fontWeight: 500, mt: 0.5 }}
              >
                {category.nombreEspanol}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Estado
                </Typography>
                <Chip
                  label={category.activo ? "Activa" : "Inactiva"}
                  color={category.activo ? "primary" : "default"}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "6px",
                    minWidth: "85px",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Editar" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(category)}
                    sx={{ color: "text.secondary" }}
                  >
                    <FiEdit />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={category.activo ? "Deshabilitar" : "Habilitar"}
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={() => onToggleStatus(category)}
                    sx={{ color: "text.secondary" }}
                  >
                    {category.activo ? <FiEyeOff /> : <FiEye />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </>
  );
}
